import type { BlockContent, PhrasingContent } from 'mdast';

import type { Node as PMNode, Mark as PMMark } from '@atlaskit/editor-prosemirror/model';

import type { FromPMHandlers } from './types';

/**
 * Wrap an mdast phrasing content node with marks, building up nested nodes
 * for strong, emphasis, code, link, and strikethrough marks.
 *
 * Annotation marks are intentionally ignored here — they are tracked as a
 * separate position map alongside the markdown string rather than being
 * embedded as syntax. See convertInlineChildren for position tracking.
 */
export function applyMarks(node: PhrasingContent, marks: readonly PMMark[]): PhrasingContent {
	let result: PhrasingContent = node;
	for (const mark of marks) {
		switch (mark.type.name) {
			case 'strong':
				result = { type: 'strong', children: [result] };
				break;
			case 'em':
				result = { type: 'emphasis', children: [result] };
				break;
			case 'code':
				// Inline code — unwrap text value
				if (result.type === 'text') {
					result = { type: 'inlineCode', value: result.value };
				}
				break;
			case 'link':
				result = {
					type: 'link',
					url: mark.attrs.href ?? '',
					title: mark.attrs.title ?? null,
					children: [result],
				};
				break;
			case 'strike':
			case 'strikethrough':
				// GFM strikethrough ~~text~~ — symmetric with the `delete` handler in to-pm
				result = { type: 'delete', children: [result] };
				break;
			default:
				// Unknown mark (including annotation) — skip
				break;
		}
	}
	return result;
}

/**
 * Convert block-level PM children to mdast BlockContent using the given handlers.
 */
export function convertBlockChildren(node: PMNode, handlers: FromPMHandlers): BlockContent[] {
	const children: BlockContent[] = [];
	node.forEach((child) => {
		const handler = handlers[child.type.name];
		if (handler) {
			const result = handler(child);
			if (result) {
				if (Array.isArray(result)) {
					children.push(...(result as BlockContent[]));
				} else {
					children.push(result as BlockContent);
				}
			}
		}
	});
	return children;
}

/**
 * Returns a stable key representing the non-annotation marks on a text node,
 * used to determine whether adjacent text nodes can be merged for serialization.
 *
 * Two text nodes are mergeable if they have the same set of non-annotation marks
 * (in the same order — ProseMirror maintains mark order by schema rank).
 * Annotation marks are excluded because they are tracked as character ranges
 * separately from the markdown string and don't affect markdown syntax.
 */
function nonAnnotationMarkKey(marks: readonly PMMark[]): string {
	return marks
		.filter((m) => m.type.name !== 'annotation')
		.map((m) => `${m.type.name}:${JSON.stringify(m.attrs)}`)
		.join('|');
}

/**
 * Convert inline PM children to mdast PhrasingContent nodes using applyMarks for each inline child.
 *
 * Adjacent text nodes that share the same non-annotation marks (differing only
 * in annotation marks) are merged into a single text run before serialization.
 * This ensures that e.g. ["b"(strong), "old"(strong+ann)] serializes as
 * `**bold**` rather than `**b****old**`, which would break the annotation
 * character-offset tracking in extractAnnotationRanges.
 */
export function convertInlineChildren(node: PMNode, handlers: FromPMHandlers): PhrasingContent[] {
	// First pass: merge adjacent text nodes with identical non-annotation marks.
	type MergedText = { marks: readonly PMMark[]; text: string };
	type MergedItem = MergedText | { pmNode: PMNode };
	const merged: MergedItem[] = [];

	node.forEach((child) => {
		if (child.isText && child.text) {
			const last = merged[merged.length - 1];
			if (
				last &&
				'text' in last &&
				nonAnnotationMarkKey(last.marks) === nonAnnotationMarkKey(child.marks)
			) {
				// Same non-annotation marks — merge text into the previous run.
				last.text += child.text;
			} else {
				merged.push({ text: child.text, marks: child.marks });
			}
		} else {
			merged.push({ pmNode: child });
		}
	});

	// Second pass: convert merged runs to mdast nodes.
	const children: PhrasingContent[] = [];
	for (const item of merged) {
		if ('pmNode' in item) {
			const handler = handlers[item.pmNode.type.name];
			if (handler) {
				const result = handler(item.pmNode);
				if (result) {
					const nodes = Array.isArray(result) ? result : [result];
					children.push(...(nodes as PhrasingContent[]));
				}
			}
		} else {
			// Use only non-annotation marks for markdown syntax — annotation marks
			// are tracked as character ranges, not embedded in the markdown string.
			const nonAnnotationMarks = item.marks.filter((m) => m.type.name !== 'annotation');
			const textNode = applyMarks({ type: 'text', value: item.text }, nonAnnotationMarks);
			children.push(textNode);
		}
	}
	return children;
}
